import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchDocument,
  fetchProjectDocs,
  setDocumentCategory,
  type Me,
  type ProjectDoc,
} from '../api';
import { Badge, Empty, ErrorBox, Loading, RelativeTime } from '../components/ui';
import { Markdown } from '../components/markdown';

/**
 * Documentación de un proyecto, espejada desde su repositorio.
 *
 * Antes el portal sabía QUÉ ficheros de documentación tiene cada proyecto pero no su
 * contenido: para leer cómo funciona un sistema había que abrir GitHub, y en la práctica
 * eso significa que no se lee.
 *
 * LA PANTALLA DICE QUE ES UN ESPEJO, y lo dice arriba del todo. No es un detalle de
 * cortesía: alguien que crea que puede editar aquí escribiría algo que la siguiente
 * sincronización borra sin avisar. El enlace a GitHub está en cada documento, que es
 * donde sí se edita.
 *
 * La CATEGORÍA sí se pone aquí, porque no tiene dónde vivir en el repositorio. Es lo
 * único que sobrevive a la sincronización.
 */
export function DocsTab({ slug, me }: { slug: string; me?: Me }): JSX.Element {
  const client = useQueryClient();
  const [busqueda, setBusqueda] = useState('');
  const [abierto, setAbierto] = useState<string | null>(null);

  const lista = useQuery({
    queryKey: ['docs', slug, busqueda],
    queryFn: () => fetchProjectDocs(slug, busqueda),
  });
  const doc = useQuery({
    queryKey: ['doc', abierto],
    queryFn: () => fetchDocument(abierto as string),
    enabled: abierto !== null,
  });

  const clasificar = useMutation({
    mutationFn: (input: { id: string; category: string | null }) =>
      setDocumentCategory(input.id, input.category),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['docs', slug] });
      void client.invalidateQueries({ queryKey: ['doc', abierto] });
    },
  });

  if (lista.isPending) return <Loading />;
  if (lista.isError) return <ErrorBox error={lista.error} />;

  const { documents, categories, map } = lista.data;
  const puedeClasificar = me?.role === 'admin' || me?.role === 'member';
  const etiqueta = (v: string | null): string =>
    categories.find((c) => c.value === v)?.label ?? 'Sin clasificar';

  // Agrupado por categoría, y las sin clasificar al final: son las que hay que mirar,
  // pero no las que se buscan.
  const grupos = new Map<string | null, ProjectDoc[]>();
  for (const d of documents) {
    const k = d.category;
    if (!grupos.has(k)) grupos.set(k, []);
    grupos.get(k)?.push(d);
  }
  const orden = [...grupos.keys()].sort((a, b) => {
    if (a === null) return 1;
    if (b === null) return -1;
    return etiqueta(a).localeCompare(etiqueta(b));
  });

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
      <div>
        {/* El mapa va antes que la lista y antes que el buscador. Con cientos de documentos
            —y en algunos proyectos, cientos de informes de fases de desarrollo— el problema
            no es encontrar: es no saber por dónde empezar. Esto es el por dónde. */}
        {map ? (
          <button
            type="button"
            onClick={() => setAbierto(map.id)}
            className={`mb-3 block w-full rounded-lg border px-3 py-2 text-left ${
              abierto === map.id
                ? 'border-[var(--color-ink)] bg-[var(--color-surface-muted)]'
                : 'border-[var(--color-border-subtle)] hover:bg-[var(--color-surface-muted)]'
            }`}
          >
            <span className="block text-[11px] uppercase tracking-wide text-[var(--color-ink-muted)]">
              Empieza aquí
            </span>
            <span className="mt-0.5 block text-[13px] font-medium">{map.title}</span>
            <span className="mt-0.5 block text-[11px] leading-snug text-[var(--color-ink-muted)]">
              Qué hace · las piezas y qué afecta a qué · qué puede fallar · lo que no es obvio
            </span>
          </button>
        ) : (
          <p className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
            <strong className="font-semibold">Este proyecto no tiene mapa.</strong> Un mapa es
            una página corta con lo que hace falta saber para operarlo: qué piezas hay, qué se
            rompe cuando cae cada una, qué puede fallar y dónde mirar. Se crea como{' '}
            <code>docs/MAPA.md</code> en el repositorio y aparece aquí en la siguiente
            sincronización.
          </p>
        )}

        <p className="mb-3 text-[12px] text-[var(--color-ink-muted)]">
          {documents.length} documento(s) más del repositorio. <strong>Es un espejo</strong>: se
          edita en el repositorio, no aquí. La categoría sí se pone desde el portal.
        </p>

        <input
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar por título o ruta…"
          className="mb-3 w-full rounded border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-2 py-1 text-[13px]"
        />

        {documents.length === 0 ? (
          <Empty>
            {busqueda
              ? 'Ningún documento coincide.'
              : 'Sin documentación espejada. Se trae con `node dist/cli.js docs --repo <slug>`.'}
          </Empty>
        ) : (
          <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
            {orden.map((cat) => (
              <div key={cat ?? 'sin'}>
                <p className="mb-1 text-[11px] uppercase tracking-wide text-[var(--color-ink-muted)]">
                  {etiqueta(cat)} · {grupos.get(cat)?.length}
                </p>
                <ul className="space-y-0.5">
                  {grupos.get(cat)?.map((d) => (
                    <li key={d.id}>
                      <button
                        type="button"
                        onClick={() => setAbierto(d.id)}
                        className={`w-full rounded px-2 py-1 text-left text-[13px] ${
                          abierto === d.id
                            ? 'bg-[var(--color-surface-muted)] font-medium'
                            : 'hover:bg-[var(--color-surface-muted)]'
                        }`}
                      >
                        <span className="block truncate">{d.title}</span>
                        <span className="block truncate text-[11px] text-[var(--color-ink-muted)]">
                          {d.path}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="min-w-0">
        {abierto === null ? (
          <Empty>Elige un documento de la lista.</Empty>
        ) : doc.isPending ? (
          <Loading />
        ) : doc.isError ? (
          <ErrorBox error={doc.error} />
        ) : (
          <article className="min-w-0">
            <header className="mb-4 border-b border-[var(--color-border-subtle)] pb-3">
              <h2 className="text-[16px] font-semibold">{doc.data.title}</h2>
              <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-[var(--color-ink-muted)]">
                <span className="font-mono">{doc.data.path}</span>
                <span>{Math.round(doc.data.bytes / 1024)} kB</span>
                <span>
                  espejado <RelativeTime iso={doc.data.synced_at} />
                </span>
                {doc.data.html_url && (
                  <a
                    href={doc.data.html_url}
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                  >
                    ver y editar en GitHub
                  </a>
                )}
              </p>

              <div className="mt-2 flex flex-wrap items-center gap-2">
                {puedeClasificar ? (
                  <label className="flex items-center gap-2 text-[12px]">
                    <span className="text-[var(--color-ink-muted)]">Categoría</span>
                    <select
                      value={doc.data.category ?? ''}
                      disabled={clasificar.isPending}
                      onChange={(e) =>
                        clasificar.mutate({
                          id: doc.data.id,
                          category: e.target.value === '' ? null : e.target.value,
                        })
                      }
                      className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-2 py-1"
                    >
                      <option value="">Sin clasificar</option>
                      {categories.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <Badge>{etiqueta(doc.data.category)}</Badge>
                )}
                {doc.data.category_set_by && (
                  <span className="text-[11px] text-[var(--color-ink-muted)]">
                    clasificado por @{doc.data.category_set_by}
                  </span>
                )}
              </div>
              {clasificar.isError && <ErrorBox error={clasificar.error} />}
            </header>

            <Markdown texto={doc.data.content} />
          </article>
        )}
      </div>
    </div>
  );
}
